import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotify, useRecordContext } from 'react-admin';
import { EXCLUDE_ERROR, FETCH_ERROR, MARK_AS_ERROR, MARK_AS_SUCCESS } from './constants';
import type { IdvExclude, IdvMarkAsPayload, IdvMarkAsResponse, IdvOpsResponse } from './types';
import { EMPTY_IDV_OPS, getAuthHeaders, getIdvExcludeUrl, getIdvOpsUrl } from './utils';

const firstErrorMessage = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (Array.isArray(value)) {
    for (const item of value) {
      const message = firstErrorMessage(item);
      if (message) return message;
    }
  }
  if (value && typeof value === 'object') {
    for (const item of Object.values(value)) {
      const message = firstErrorMessage(item);
      if (message) return message;
    }
  }
  return null;
};

const useController = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [ops, setOps] = useState<IdvOpsResponse>(EMPTY_IDV_OPS);
  const [loading, setLoading] = useState(false);
  const [excludeSaving, setExcludeSaving] = useState(false);
  const [markSavingId, setMarkSavingId] = useState<IdvMarkAsPayload['attempt_id'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const excludeSavingRef = useRef(false);
  const markSavingRef = useRef(false);

  const workerId = record?.id;

  const fetchOps = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!workerId) return;
      const silent = options?.silent === true;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const response = await fetch(getIdvOpsUrl(workerId), {
          headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error(FETCH_ERROR);
        const data = (await response.json()) as IdvOpsResponse;
        setOps({
          ...EMPTY_IDV_OPS,
          ...data,
          attempts: data.attempts ?? [],
          history: data.history ?? [],
        });
      } catch (err) {
        console.error(FETCH_ERROR, err);
        if (!silent) {
          setError(FETCH_ERROR);
          setOps(EMPTY_IDV_OPS);
          notify(FETCH_ERROR, { type: 'warning' });
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [workerId, notify]
  );

  const setExcluded = useCallback(
    async (excluded: boolean) => {
      if (!workerId || excludeSavingRef.current) return;
      excludeSavingRef.current = true;
      setExcludeSaving(true);

      const previous = ops.exclude;
      setOps((current) => ({
        ...current,
        exclude: { ...current.exclude, excluded },
      }));

      try {
        const response = await fetch(getIdvExcludeUrl(workerId), {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ excluded }),
        });
        const data = (await response.json().catch(() => null)) as IdvExclude | unknown;
        if (!response.ok) throw new Error(firstErrorMessage(data) ?? EXCLUDE_ERROR);
        const exclude = data as IdvExclude;
        setOps((current) => ({
          ...current,
          exclude: {
            excluded: exclude.excluded,
            exclude_auto_withdraw_by: exclude.exclude_auto_withdraw_by ?? null,
            exclude_auto_withdraw_at: exclude.exclude_auto_withdraw_at ?? null,
          },
          ...(exclude.policy ? { policy: exclude.policy } : {}),
        }));
        await fetchOps({ silent: true });
      } catch (err) {
        const message = err instanceof Error && err.message ? err.message : EXCLUDE_ERROR;
        console.error(EXCLUDE_ERROR, err);
        setOps((current) => ({ ...current, exclude: previous }));
        notify(message, { type: 'warning' });
      } finally {
        excludeSavingRef.current = false;
        setExcludeSaving(false);
      }
    },
    [workerId, notify, ops.exclude, fetchOps]
  );

  const markAttempt = useCallback(
    async (payload: IdvMarkAsPayload): Promise<boolean> => {
      if (!workerId || markSavingRef.current || !payload.comment.trim()) return false;
      markSavingRef.current = true;
      setMarkSavingId(payload.attempt_id);

      try {
        const response = await fetch(getIdvOpsUrl(workerId), {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        });
        const data = (await response.json().catch(() => null)) as IdvMarkAsResponse | unknown;
        if (!response.ok) {
          throw new Error(firstErrorMessage(data) ?? MARK_AS_ERROR);
        }

        const result = data as IdvMarkAsResponse;
        setOps((current) => ({
          ...current,
          bgc_status: result.bgc_status,
          resolution: result.resolution,
          ...(result.read_only != null ? { read_only: result.read_only } : {}),
          ...(result.read_only_reason !== undefined ? { read_only_reason: result.read_only_reason } : {}),
          ...(result.actions ? { actions: result.actions } : {}),
          ...(result.policy ? { policy: result.policy } : {}),
          attempts: current.attempts.map((attempt) => {
            const nextAttempt = String(attempt.id) === String(result.attempt.id) ? result.attempt : attempt;
            return result.resolution.resolved_attempt_id != null ? { ...nextAttempt, can_mark_as: false } : nextAttempt;
          }),
          history: [...(result.history ?? []), ...current.history],
        }));
        notify(MARK_AS_SUCCESS, { type: 'success' });
        return true;
      } catch (err) {
        const message = err instanceof Error && err.message ? err.message : MARK_AS_ERROR;
        console.error(MARK_AS_ERROR, err);
        notify(message, { type: 'warning' });
        return false;
      } finally {
        markSavingRef.current = false;
        setMarkSavingId(null);
      }
    },
    [workerId, notify]
  );

  useEffect(() => {
    fetchOps();
  }, [fetchOps]);

  return {
    ops,
    loading,
    excludeSaving,
    markSavingId,
    error,
    refetch: fetchOps,
    setExcluded,
    markAttempt,
  };
};

export default useController;
